import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">A1C Survival Plan</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Terms of Service</h1>
          <p className="text-xl text-teal-600 font-medium mb-2">A1C Survival Plan</p>
          <p className="text-slate-500 mb-8">Effective Date: December 01, 2025</p>

          <p className="text-slate-600 leading-relaxed">
            Welcome to A1C Survival Plan. By downloading, accessing, or using the app, you agree to these 
            Terms of Service ("Terms"). If you do not agree, please do not use the app.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">1. Purpose of the App</h2>
          <p className="text-slate-600 leading-relaxed">
            A1C Survival Plan is a personal journal-centered organization app designed to help users record, 
            track, organize, and reflect on daily lifestyle factors and experiences related to diabetes 
            management and wellness. The app provides organizational tools, insights, and visualizations — not medical care.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">2. Not Medical Advice</h2>
          <p className="text-slate-600 leading-relaxed font-medium">
            A1C Survival Plan does not provide medical advice, diagnosis, or treatment.
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>Information provided in the app is for informational and self-reflection purposes only</li>
            <li>Always consult a qualified healthcare professional before making medical decisions</li>
            <li>Do not disregard professional medical advice based on app content</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">3. User Responsibilities</h2>
          <p className="text-slate-600 leading-relaxed">
            By using the app, you agree to:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>Provide accurate information to the best of your ability</li>
            <li>Use the app for personal, lawful purposes only</li>
            <li>Understand that insights are based on user-entered data and patterns, not clinical analysis</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4 font-medium">
            You are responsible for decisions you make using information from the app.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">4. Accounts & Access</h2>
          <p className="text-slate-600 leading-relaxed">
            Some features may require account creation.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activity that occurs under your account</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4">
            We reserve the right to suspend or terminate accounts that violate these Terms.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">5. Data Ownership</h2>
          <p className="text-slate-600 leading-relaxed">
            You retain ownership of all data you enter into A1C Survival Plan, including journal entries 
            and health-related logs.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            By using the app, you grant us permission to process your data only as described in our{' '}
            <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</Link>.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">6. Intellectual Property</h2>
          <p className="text-slate-600 leading-relaxed">
            All app content, design, software, and branding are the property of A1C Survival Plan or its licensors.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You may not copy, modify, distribute, or reverse engineer any part of the app without permission.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">7. Third-Party Services</h2>
          <p className="text-slate-600 leading-relaxed">
            The app may integrate with third-party services (such as Apple Health).
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Use of third-party services is governed by their own terms and policies. We are not responsible 
            for third-party content or services.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">8. Service Availability</h2>
          <p className="text-slate-600 leading-relaxed">
            We strive to keep the app available, but:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>We do not guarantee uninterrupted or error-free operation</li>
            <li>Features may change, be added, or removed over time</li>
            <li>Temporary outages may occur for maintenance or updates</li>
          </ul>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-600 leading-relaxed">
            To the maximum extent permitted by law:
          </p>
          <p className="text-slate-600 leading-relaxed mt-4 font-medium">
            A1C Survival Plan is provided "as is" without warranties of any kind.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            We are not liable for:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>Health outcomes</li>
            <li>Decisions made based on app insights</li>
            <li>Data loss</li>
            <li>Indirect or consequential damages</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4 font-medium">
            Your use of the app is at your own risk.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">10. Termination</h2>
          <p className="text-slate-600 leading-relaxed">
            You may stop using the app at any time.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            We may suspend or terminate access if:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
            <li>These Terms are violated</li>
            <li>Required by law</li>
            <li>The service is discontinued</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mt-4">
            Upon termination, data handling will follow our{' '}
            <Link to="/privacy-policy" className="text-teal-600 hover:text-teal-700">Privacy Policy</Link>.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">11. Changes to These Terms</h2>
          <p className="text-slate-600 leading-relaxed">
            We may update these Terms as the app evolves.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Material changes will be communicated within the app or on our website. Continued use 
            constitutes acceptance of updated Terms.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">12. Governing Law</h2>
          <p className="text-slate-600 leading-relaxed">
            These Terms are governed by the laws of [Your State / Country], without regard to conflict of law principles.
          </p>

          <hr className="my-8 border-slate-200" />

          <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">13. Contact Information</h2>
          <p className="text-slate-600 leading-relaxed">
            If you have questions about these Terms, contact:
          </p>
          <div className="bg-slate-50 rounded-2xl p-6 mt-4">
            <p className="text-slate-600">
              <span className="font-medium">Email:</span>{' '}
              <a href="mailto:support@a1csurvivalplan.com" className="text-teal-600 hover:text-teal-700">
                support@a1csurvivalplan.com
              </a>
            </p>
            <p className="text-slate-600 mt-2">
              <span className="font-medium">App Name:</span> A1C Survival Plan
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} A1C Survival Plan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
